import {LobbyImage} from '@colonist/types'
import {createDivIn, createImgIn, createParagraphIn} from '../../ui_utils/DomUtils'
import {TranslationObject} from '@colonist/i18n'
import {createTooltipIn, disableTooltip} from '../../ui_utils/TooltipUtils.ts'

export enum BannerType {
    Premium,
    WeeklyFree
}

export interface ItemData {
    name: TranslationObject
    image: LobbyImage
    available: boolean
    selected: boolean
    clickAction: () => void
    infoAction?: () => void
    banner?: BannerType | undefined
}

export class UIComponentItemPanelCell {
    readonly container: HTMLDivElement

    private readonly name: TranslationObject
    private readonly image: HTMLImageElement
    private readonly available: boolean
    private readonly selected: boolean
    private readonly banner: BannerType | undefined
    private readonly clickAction: () => void
    private readonly infoAction: (() => void) | undefined

    constructor(container: HTMLDivElement, data: ItemData) {
        this.container = createDivIn(container, 'item-panel-cell')
        this.name = data.name
        this.image = createImgIn(this.container, data.image, 'item-panel-cell-image')
        this.available = data.available
        this.selected = data.selected
        this.banner = data.banner
        this.clickAction = data.clickAction
        this.infoAction = data.infoAction
        this.init()
    }

    updateInteractability(isGuest: boolean) {
        if(isGuest) {
            this.container.classList.add('no-interaction')
            this.setInfoOnClick()
            return
        }
        this.container.classList.remove('no-interaction')
        this.setOnClick()
    }

    private init() {
        this.updateCellView()
        this.setTooltip()
        this.setOnClick()
    }

    private updateCellView() {
        if(this.available == false) this.setUnavailable()
        if(this.selected) this.setSelected()
        this.setBanner()
        this.image.draggable = false
    }

    private setUnavailable() {
        createImgIn(this.container, LobbyImage.LockOutlined, 'item-panel-cell-lock')
    }

    private setSelected() {
        this.container.classList.add('selected')
    }

    private setOnClick() {
        this.container.onclick = this.clickAction
    }

    private setInfoOnClick() {
        if(this.infoAction == undefined) {
            this.container.onclick = () => undefined
            return
        }
        this.container.onclick = this.infoAction
    }

    private setTooltip() {
        createTooltipIn(this.container, this.name, undefined, 'small')
    }

    removeTooltip() {
        disableTooltip(this.container)
    }

    private setBanner() {
        if(this.banner == undefined) return
        const banner = createDivIn(this.container, `item-panel-cell-banner ${this.getBannerClass()}`)
        const text = this.getBannerText()
        createParagraphIn(banner, text, 'item-cell-banner-text')
    }

    private getBannerClass(): string {
        switch(this.banner) {
            case BannerType.Premium: return 'premium'
            case BannerType.WeeklyFree: return 'weekly-free'
            default: return ''
        }
    }

    private getBannerText(): TranslationObject {
        switch(this.banner) {
            case BannerType.Premium: return {
                key: 'strings:storePage.membership.title.premium',
                options: {
                    context: 'capitalized',
                },
            }
            case BannerType.WeeklyFree: return {
                key: 'strings:roomPage.options.map.weeklyFree',
                options: {
                    context: 'capitalized',
                },
            }
            default: return {key: 'strings:colonistCommon.utils.emptyString'}
        }
    }
}
